import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Heart, MessageCircle, MapPin, Calendar, Play, Pause, BellRing, Tag, Info, PartyPopper, BadgeCheck, Zap } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import ShareButton from '@/components/ShareButton';
import ReactionBar from '@/components/ReactionBar';
import ReportButton from '@/components/ReportButton';
import PollDisplay from '@/components/PollDisplay';

export const CATEGORY_META = {
  alerte: { label: 'Alerte', icon: BellRing, badge: 'badge-alerte' },
  evenement: { label: 'Événement', icon: PartyPopper, badge: 'badge-evenement' },
  bon_plan: { label: 'Bon plan', icon: Tag, badge: 'badge-bon_plan' },
  info: { label: 'Info', icon: Info, badge: 'badge-info' },
};

export const CategoryBadge = ({ category, className = '' }) => {
  const meta = CATEGORY_META[category] || { label: category, icon: Info, badge: 'badge-info' };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider ${meta.badge} ${className}`}>
      <meta.icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
};

export const PostCardSkeleton = () => (
  <div className="post-card p-5">
    <div className="flex items-center gap-3 mb-4">
      <div className="skeleton h-10 w-10 rounded-full" />
      <div className="flex-1 space-y-2"><div className="skeleton h-3.5 w-1/3" /><div className="skeleton h-3 w-1/4" /></div>
    </div>
    <div className="skeleton h-5 w-3/4 mb-3" />
    <div className="skeleton h-3.5 w-full mb-2" />
    <div className="skeleton h-3.5 w-2/3 mb-4" />
    <div className="skeleton h-40 w-full rounded-xl" />
  </div>
);

const AudioPlayer = ({ post, playingAudio, setPlayingAudio }) => {
  const playing = playingAudio === post.post_id;
  const toggle = (e) => {
    e.stopPropagation();
    if (playing) { document.getElementById(`audio-${post.post_id}`)?.pause(); setPlayingAudio(null); return; }
    if (playingAudio) document.getElementById(`audio-${playingAudio}`)?.pause();
    document.getElementById(`audio-${post.post_id}`)?.play();
    setPlayingAudio(post.post_id);
  };
  return (
    <div className="flex items-center gap-3 p-3 bg-muted/60 rounded-xl mb-4" onClick={(e) => e.stopPropagation()}>
      <button onClick={toggle} className="h-10 w-10 rounded-full lagoon-gradient text-white inline-flex items-center justify-center shadow btn-bounce" data-testid={`audio-play-${post.post_id}`}>
        {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
      </button>
      <div className="flex-1">
        <p className="text-sm font-medium">Message audio</p>
        <audio id={`audio-${post.post_id}`} src={post.audio_url} onEnded={() => setPlayingAudio(null)} className="w-full h-8" />
      </div>
    </div>
  );
};

// Shared post card (feed, profile, commune, search)
const PostCard = ({ post, api, onUpdate, onLike, onVote, playingAudio, setPlayingAudio, compact = false }) => {
  const navigate = useNavigate();
  const update = (fields) => onUpdate?.(post.post_id, fields);

  const like = async (e) => {
    e.stopPropagation();
    if (onLike) return onLike(post.post_id);
    try {
      const { data } = await api.post(`/posts/${post.post_id}/like`);
      update({ is_liked: data.liked, likes_count: data.likes_count });
    } catch { /* handled by caller toast */ }
  };

  return (
    <article
      className={`post-card cursor-pointer group ${post.is_boosted ? 'ring-1 ring-gold/60' : ''}`}
      onClick={() => navigate(`/post/${post.post_id}`)}
      data-testid={`post-card-${post.post_id}`}
    >
      {post.is_boosted && (
        <div className="flex items-center gap-1.5 px-5 py-1.5 bg-gold/15 text-[11px] font-mono font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
          <Zap className="h-3 w-3" /> Sponsorisé
        </div>
      )}
      <div className={compact ? 'p-4' : 'p-5'}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <button
            className="flex items-center gap-3 min-w-0 text-left"
            onClick={(e) => { e.stopPropagation(); navigate(`/profile/${post.user_id}`); }}
            data-testid={`post-author-${post.post_id}`}
          >
            <Avatar className="h-10 w-10 ring-2 ring-background shadow">
              <AvatarImage src={post.user_picture} />
              <AvatarFallback className="bg-secondary/15 text-secondary font-bold">{post.user_name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="font-semibold text-sm leading-tight truncate flex items-center gap-1">
                {post.user_name}
                {post.is_verified_commerce && <BadgeCheck className="h-4 w-4 text-secondary" />}
              </p>
              <p className="flex items-center gap-1 text-xs text-muted-foreground truncate">
                <MapPin className="h-3 w-3 shrink-0" />
                {post.commune}{post.quartier ? ` · ${post.quartier}` : ''}
                <span className="mx-1">·</span>
                {formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: fr })}
              </p>
            </div>
          </button>
          <CategoryBadge category={post.category} />
        </div>

        <h3 className="text-lg font-bold leading-snug mb-1.5 group-hover:text-primary transition-colors">{post.title}</h3>
        <p className={`text-[15px] text-foreground/80 leading-relaxed mb-4 ${compact ? 'line-clamp-2' : 'line-clamp-3'}`}>{post.content}</p>

        {post.image_url && post.image_url.trim() !== '' && (
          <div className="relative mb-4 rounded-xl overflow-hidden bg-muted">
            <img
              src={post.image_url}
              alt={post.title}
              loading="lazy"
              className={`w-full object-cover transition-transform duration-500 group-hover:scale-[1.02] ${compact ? 'h-40' : 'h-56 sm:h-64'}`}
              onError={(e) => { e.target.parentElement.style.display = 'none'; }}
            />
          </div>
        )}

        {post.audio_url && post.audio_url.trim() !== '' && (
          <AudioPlayer post={post} playingAudio={playingAudio} setPlayingAudio={setPlayingAudio} />
        )}

        {post.event_date && (
          <div className="inline-flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-300 bg-amber-500/10 rounded-lg px-3 py-1.5 mb-4">
            <Calendar className="h-4 w-4" />
            {new Date(post.event_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
          </div>
        )}

        {post.poll_options && post.poll_options.length > 0 && (
          <div onClick={(e) => e.stopPropagation()}><PollDisplay post={post} onVote={onVote} /></div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-border/60">
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={like}
              className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors hover:bg-muted ${post.is_liked ? 'text-primary' : ''}`}
              data-testid={`like-btn-${post.post_id}`}
            >
              <Heart className={`h-4 w-4 ${post.is_liked ? 'fill-current' : ''}`} />
              {post.likes_count}
            </button>
            <ReactionBar post={post} onChange={update} />
            <span className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-muted-foreground" data-testid={`comments-count-${post.post_id}`}>
              <MessageCircle className="h-4 w-4" />
              {post.comments_count}
            </span>
          </div>
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <ShareButton
              post={post}
              onShare={async () => { try { await api.post(`/posts/${post.post_id}/share`); } catch (e) { console.error('Share error:', e); } }}
            />
            <ReportButton postId={post.post_id} type="post" />
          </div>
        </div>
      </div>
    </article>
  );
};

export default PostCard;
